import './index.css';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { reportWebVitals } from './reportWebVitals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppStateProvider, AppStateEffect } from './state/AppState';
import { RetrievalStateProvider } from './state/RetrievalState';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';

import AssistantPage from './pages/Assistant';
import ChatPage from './pages/Chat';
import DevPage from './pages/Dev';
import KnowledgePage from './pages/Knowledge';
import NoPage from './pages/nopage/NoPage';
import Layout from './pages/layout/Layout';
import SignInPage from './pages/auth/SignIn';
import SignUpPage from './pages/auth/SignUp';
import MagicLinkConfirmPage from './pages/auth/MagicLinkConfirm';
import ProfilePage from './pages/Profile';
import { SettingsLayout } from './pages/Settings/SettingsLayout';
import { AccountSection } from './pages/Settings/sections/AccountSection';
import { OrganizationSection } from './pages/Settings/sections/OrganizationSection';
import { PreferencesSection } from './pages/Settings/sections/PreferencesSection';
import { AdminSettingsSection } from './pages/Settings/sections/AdminSettingsSection';
import { WorkspaceSection } from './pages/Settings/sections/WorkspaceSection';
import {
  LLMConfiguration,
  IntegrationsConfiguration,
  ConnectorsConfiguration,
  ApprovalRolesConfiguration,
  PolicyConfiguration,
  ObservabilityConfiguration,
} from './pages/Settings/sections/workspace';
import EditorPage from './pages/Editor/EditorPage';
import {
  AdminLayout,
  DashboardPage as AdminDashboardPage,
  MembersPage as AdminMembersPage,
  InvitesPage as AdminInvitesPage,
  KeycloakSyncPage as AdminKeycloakSyncPage,
  BillingPage as AdminBillingPage,
  AuditPage as AdminAuditPage,
} from './pages/Admin';
import MessagesPage from './pages/Messages/MessagesPage';

const queryClient = new QueryClient();

/**
 * The main wrapper for the app.
 * Implements the routing system of the app.
 * Each route is given a path and an element to render when that path is visited.
 */
export const AppWrapper = () => (
  <QueryClientProvider client={queryClient}>
    <AppStateProvider>
      <RetrievalStateProvider>
        <AuthProvider>
          <BrowserRouter basename="/">
            <Routes>
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/auth/magic-link" element={<MagicLinkConfirmPage />} />
              <Route path="/dev" element={<DevPage />} />

              <Route element={<Layout />}>
                <Route path="/" element={<AssistantPage />} />
                <Route path="/knowledge" element={<KnowledgePage />} />
                <Route path="/editor" element={<EditorPage />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/settings" element={<SettingsLayout />}>
                  <Route index element={<Navigate to="/settings/account" replace />} />
                  <Route path="account" element={<AccountSection />} />
                  <Route path="organization" element={<OrganizationSection />} />
                  <Route path="preferences" element={<PreferencesSection />} />
                  <Route path="admin" element={<AdminSettingsSection />} />
                  <Route path="workspace" element={<WorkspaceSection />}>
                    <Route index element={<Navigate to="/settings/workspace/llm" replace />} />
                    <Route path="llm" element={<LLMConfiguration />} />
                    <Route path="integrations" element={<IntegrationsConfiguration />} />
                    <Route path="connectors" element={<ConnectorsConfiguration />} />
                    <Route path="approvals" element={<ApprovalRolesConfiguration />} />
                    <Route path="policy" element={<PolicyConfiguration />} />
                    <Route path="observability" element={<ObservabilityConfiguration />} />
                  </Route>
                </Route>

                {/* Admin routes — nested under AdminLayout */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboardPage />} />
                  <Route path="members" element={<AdminMembersPage />} />
                  <Route path="invites" element={<AdminInvitesPage />} />
                  <Route path="billing" element={<AdminBillingPage />} />
                  <Route path="keycloak" element={<AdminKeycloakSyncPage />} />
                  <Route path="audit" element={<AdminAuditPage />} />
                </Route>

                {/* Messaging */}
                <Route path="/messages" element={<MessagesPage />} />
                <Route path="/messages/:channelId" element={<MessagesPage />} />

                {/* This is the 404 route - routes any path that does not match the previous ones */}
                <Route path="*" element={<NoPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <AppStateEffect />
        </AuthProvider>
      </RetrievalStateProvider>
    </AppStateProvider>
  </QueryClientProvider>
);

/**
 * The main render function for the app. Renders the AppWrapper component in a strict mode.
 */
ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <AppWrapper />
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
